<script setup lang="ts">
import {storeToRefs} from "pinia";
import {useSystemStore} from "@/stores/system.store";
import {useExtensionStore} from "@/stores/extension.store";
import {useRoute, useRouter} from "vue-router";
import {computed, ref, watch} from "vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";
import ExtensionNavigationService from "@/extension/services/ExtensionNavigationService";
import {ExtensionGrid} from "@/extension/components/extension-grid";
import {LaboratoryPartnershipTypeSlug} from "@/core/interfaces/laboratory/LaboratoryPartnershipType";
import {EntityTypeID} from "@/core/interfaces/Entity";
import {laboratoryApi} from "@/container";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import Extension from "@/core/interfaces/extension/Extension";
import {Play} from "@lucide/vue";
import VideoItem from "@/video/components/VideoItem.vue";
import LaboratoryPartnership from "@/core/interfaces/laboratory/LaboratoryPartnership";
import DisruptionPartnerItem from "@/disruption-logistics/components/disruption-partners/DisruptionPartnerItem.vue";
import Icon from "@/core/components/icon/Icon.vue";

const {environment} = storeToRefs(useSystemStore());
const {extensions} = storeToRefs(useExtensionStore());
const router = useRouter();
const route = useRoute();

const loadingLaboratory = ref<boolean>(false);
const laboratory = ref<LaboratoryPartnership | null>();

const sponsor = computed(() => {
    const sponsorId = Number(route.params.sponsorId);

    return environment.value?.entity.sponsorshipSponsors?.find((sponsor) => {
        return sponsor.id === sponsorId;
    });
});

watch(() => sponsor.value, (_sponsor) => {
    if (_sponsor?.entityTypeId === EntityTypeID.LABORATORY) {
        loadingLaboratory.value = true;


        laboratoryApi().read(_sponsor.entityId, ['partnershipTypes', 'disruptionAlert', 'promotedAdvantages'])
            .then((response) => {
                laboratory.value = response;
            })
            .finally(() => {
                loadingLaboratory.value = false;
            });
    }
}, {immediate: true});

const hasBrandingSpacePartnershipType = computed(() => {
    if (!laboratory.value?.partnershipTypes?.length) {
        return false;
    }

    return laboratory.value.partnershipTypes.some((partnershipType) => {
        return partnershipType.slug === LaboratoryPartnershipTypeSlug.BRANDING_SPACE
            || partnershipType.slug === LaboratoryPartnershipTypeSlug.ETHICAL_BRANDING_SPACE;
    });
});

const sponsorExtensions = computed(() => {
    if (!sponsor.value || !extensions.value) {
        return [];
    }

    const extensionIds = sponsor.value.extensions.map((extension) => {
        return extension.id;
    });

    return extensions.value.filter((extension) => {
        return extensionIds.includes(extension.id);
    }).sort((a: Extension, b: Extension) => {
        const aIndex = a.subscription_plan_id ?? 0;
        const bIndex = b.subscription_plan_id ?? 0;

        return aIndex - bIndex;
    });
});

/**
 * Book an appointment about the sponsor.
 */
const bookAppointment = () => {
    if (sponsor.value?.booking_url) {
        window.open(sponsor.value.booking_url, "_blank");
    }
};

/**
 * Access the disruption page.
 */
const accessDisruption = () => {
    router.push({name: 'disruption-logistics'});
};
</script>

<template>
  <BaseContainer class="p-6">
    <BaseFlexSpinner v-if="loadingLaboratory" />
    <div v-else class="space-y-6">
      <div v-if="sponsor" class="p-6 bg-white rounded-lg">
        <div class="flex flex-col md:flex-row gap-6 items-start">
          <div v-if="sponsor.entity.branding?.logo?.renderPath" class="shrink-0">
            <img :src="sponsor.entity.branding.logo.renderPath" alt="Logo" class="h-20 w-auto">
          </div>

          <div class="flex-1 space-y-2">
            <h1 class="text-2xl font-bold text-gray-900">
              {{ sponsor.entity.name }}
            </h1>
            <p v-if="sponsor?.description" class="text-gray-600">
              {{ sponsor.description }}
            </p>

            <div v-if="sponsor.videos?.length" class="flex flex-wrap gap-3">
              <VideoItem
                v-for="video in sponsor.videos"
                :key="video.id"
                :video="video"
                class="bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 transition-all duration-300 font-medium rounded-lg shadow-sm"
              >
                <template #icon>
                  <Play class="fill-[#FB0129] text-[#FB0129] shrink-0" />
                </template>
              </VideoItem>
            </div>
          </div>
        </div>
      </div>

      <div class="pl-3">
        <ExtensionGrid
          title="Modules du partenariat"
          :extensions="sponsorExtensions"
          @extension-click="extension => ExtensionNavigationService.navigate(extension, router, route)"
        />
      </div>

      <div
        v-if="laboratory?.promotedAdvantages"
        class="bg-secondary/30 rounded-lg shadow-md overflow-hidden"
      >
        <div class="p-6">
          <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div class="flex-1 space-y-3">
              <h3 class="text-xl font-semibold text-gray-900">
                Que devient mon avantage si je passe premium ?
              </h3>
              <div class="space-y-2">
                <div
                  v-for="advantage in laboratory.promotedAdvantages"
                  :key="advantage.id"
                >
                  <p class="text-primary">
                    {{ advantage.sales_pitch }}
                  </p>
                </div>
              </div>
            </div>
            <div class="shrink-0">
              <div class="bg-white/50 p-4 rounded-full">
                <Icon name="mdi-crown" color="primary" :size="48" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="hasBrandingSpacePartnershipType && sponsor"
        class="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
      >
        <div class="p-6">
          <div class="flex flex-col md:flex-row items-center gap-6">
            <div class="flex-1 space-y-2">
              <h3 class="text-xl font-semibold text-gray-900">
                <Icon name="mdi-handshake" :size="24" color="primary" />
                Espace {{ sponsor.entity.name }}
              </h3>
              <p class="text-gray-600">
                Découvrez aussi les outils à votre disposition dans votre espace {{ sponsor.entity.name }}
              </p>
            </div>
            <BaseButton
              density="comfortable"
              class="shrink-0 shadow-sm hover:shadow-md transition-all duration-300 text-white"
              :style="{backgroundColor: laboratory?.color ? laboratory.color : '#3513e2'}"
              @click="$router.push({name: 'laboratory', params: {id: sponsor.entityId}})"
            >
              Accéder à l'espace
            </BaseButton>
          </div>
        </div>
      </div>

      <DisruptionPartnerItem
        v-if="laboratory && !!laboratory.disruptionAlert"
        :partnership-types="laboratory.partnershipTypes"
        :alert="laboratory.disruptionAlert"
        :partner-name="sponsor?.entity.name"
        :branding="sponsor?.entity.branding"
      >
        <div
          class="bg-blue-100 p-6 rounded-lg flex justify-between items-center gap-3 hover:shadow-md transition-shadow duration-300"
        >
          <div class="flex flex-col">
            <h3 class="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Icon name="mdi-truck" :size="24" color="primary" />
              Luttez contre les ruptures
            </h3>
            <span class="text-gray-700 font-medium">
              Découvrir les outils {{ sponsor?.entity.name }} pour vous aider à lutter contre les ruptures
            </span>
          </div>
          <BaseButton
            density="comfortable"
            primary
            class="shrink-0 shadow-sm hover:shadow-md transition-all duration-300"
            @click="accessDisruption"
          >
            Accéder à la rupture
          </BaseButton>
        </div>
      </DisruptionPartnerItem>

      <div
        v-if="sponsor?.booking_url"
        class="bg-primary/15 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
      >
        <div class="p-6">
          <div class="flex flex-col md:flex-row items-center gap-6">
            <div class="flex-1 space-y-2">
              <h3 class="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Icon name="mdi-calendar-clock" :size="24" color="primary" />
                Prendre rendez-vous
              </h3>
              <p class="text-gray-600">
                {{ sponsor.booking_description }}
              </p>
            </div>
            <BaseButton
              density="comfortable"
              primary
              class="shrink-0 shadow-sm hover:shadow-md transition-all duration-300"
              @click="bookAppointment"
            >
              Réserver un créneau
            </BaseButton>
          </div>
        </div>
      </div>
    </div>
  </BaseContainer>
</template>
